import React, { useEffect, useRef, useState, useCallback } from 'react';

interface BuilderLoadingScreenProps {
  onComplete: () => void;
}

const IMAGES = [
  '/pagesassets/images/template-1.webp',
  '/pagesassets/images/template-2.webp',
  '/pagesassets/images/template-3.webp',
  '/pagesassets/images/template-4.webp',
  '/pagesassets/images/template-5.webp',
  '/pagesassets/images/template-6.webp',
  '/pagesassets/images/template-7.webp',
];

const NUM_CARDS = 5; // simultaneous drifting cards

function rand(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

// Generate a random position that keeps the card fully on screen using px-aware bounds
function randomPos(w = 220, h = 165) {
  const sw = window.innerWidth;
  const sh = window.innerHeight;
  // Convert card size to %, then compute max % so card fits fully
  const maxX = Math.max(5, ((sw - w - 20) / sw) * 100);
  const maxY = Math.max(5, ((sh - h - 80) / sh) * 100); // 80px bottom reserved for progress bar
  return {
    x: rand(2, maxX),
    y: rand(2, maxY),
  };
}

interface CardState {
  id: number;
  imgIndex: number;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  startTime: number;
  duration: number;   // ms to travel from→to
  width: number;
  height: number;
  rotation: number;
  blur: number;
  opacity: number;
  zIndex: number;
  scale: number;
}

function makeCard(id: number, now: number, imgOffset: number): CardState {
  const isFeatured = id % NUM_CARDS === 0;
  const w = isFeatured ? rand(240, 280) : rand(160, 210);
  const h = isFeatured ? rand(185, 215) : rand(125, 165);
  const from = randomPos(w, h);
  const to = randomPos(w, h);
  // Ensure decent travel distance
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist < 30) {
    const sw = window.innerWidth;
    const sh = window.innerHeight;
    to.x = Math.min(((sw - w - 20) / sw) * 100, Math.max(2, from.x + (dx >= 0 ? 40 : -40)));
    to.y = Math.min(((sh - h - 80) / sh) * 100, Math.max(2, from.y + (dy >= 0 ? 35 : -35)));
  }
  return {
    id,
    imgIndex: (id + imgOffset) % IMAGES.length,
    fromX: from.x,
    fromY: from.y,
    toX: to.x,
    toY: to.y,
    startTime: now,
    duration: rand(isFeatured ? 3200 : 3800, isFeatured ? 4500 : 6000),
    width: w,
    height: h,
    rotation: rand(-8, 8),
    blur: isFeatured ? 0 : rand(10, 22),
    opacity: isFeatured ? 1 : rand(0.45, 0.75),
    zIndex: isFeatured ? 10 : Math.floor(rand(2, 6)),
    scale: isFeatured ? rand(1.0, 1.05) : 1,
  };
}

export const BuilderLoadingScreen: React.FC<BuilderLoadingScreenProps> = ({ onComplete }) => {
  const [exiting, setExiting] = useState(false);
  const [cards, setCards] = useState<CardState[]>(() => {
    const now = performance.now();
    return Array.from({ length: NUM_CARDS }, (_, i) => makeCard(i, now, i));
  });
  const imgCounterRef = useRef(NUM_CARDS);
  const rafRef = useRef<number>(0);
  const [tick, setTick] = useState(0); // drives re-render for smooth animation

  // RAF loop — re-render each frame for smooth position interpolation
  useEffect(() => {
    let running = true;
    const loop = () => {
      if (!running) return;
      setTick(t => t + 1);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      running = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // When a card completes its journey, spawn a new one with the next image
  const now = performance.now();
  const liveCards = cards.map(card => {
    const elapsed = now - card.startTime;
    if (elapsed >= card.duration) {
      // Respawn: new journey from current end position
      imgCounterRef.current += 1;
      const newCard = makeCard(imgCounterRef.current, now, imgCounterRef.current);
      // Start from where the old card ended
      newCard.fromX = card.toX;
      newCard.fromY = card.toY;
      return newCard;
    }
    return card;
  });

  // Sync state when cards are replaced (avoid stale state)
  const needsSync = liveCards.some((c, i) => c.id !== cards[i].id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (needsSync) setCards(liveCards);
  });

  // Exit + complete timers
  useEffect(() => {
    const t1 = setTimeout(() => setExiting(true), 2850);
    const t2 = setTimeout(() => onComplete(), 3300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onComplete]);

  return (
    <>
      <style>{`
        @keyframes bl-progress {
          from { width: 0%; }
          to   { width: 100%; }
        }
        @keyframes bl-fade-in  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes bl-fade-out { from { opacity: 1; } to { opacity: 0; } }
        .bl-screen      { animation: bl-fade-in  0.35s ease-out both; }
        .bl-screen.exit { animation: bl-fade-out 0.45s ease-in  both; }
      `}</style>

      <div
        className={`bl-screen fixed inset-0 flex flex-col items-center justify-end pb-14${exiting ? ' exit' : ''}`}
        style={{
          background: 'linear-gradient(145deg,#eef2ff 0%,#e0e7ff 25%,#f5f0ff 55%,#eff8ff 100%)',
          zIndex: 9999,
          overflow: 'hidden',
        }}
      >
        {/* Drifting cards */}
        {liveCards.map((card) => {
          const elapsed = Math.min(now - card.startTime, card.duration);
          // Ease in-out cubic
          const t = elapsed / card.duration;
          const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
          const x = card.fromX + (card.toX - card.fromX) * ease;
          const y = card.fromY + (card.toY - card.fromY) * ease;

          return (
            <div
              key={card.id}
              style={{
                position: 'absolute',
                left: `${x}%`,
                top: `${y}%`,
                width: card.width,
                zIndex: card.zIndex,
                opacity: card.opacity,
                filter: card.blur > 0 ? `blur(${card.blur}px)` : 'none',
                transform: `rotate(${card.rotation}deg) scale(${card.scale})`,
                willChange: 'transform, left, top',
                transition: 'opacity 0.3s ease',
              }}
            >
              <div style={{
                width: '100%',
                borderRadius: card.zIndex === 10 ? 14 : 11,
                overflow: 'hidden',
                boxShadow: card.zIndex === 10
                  ? '0 28px 64px rgba(0,0,0,0.20), 0 4px 16px rgba(0,0,0,0.10)'
                  : '0 8px 24px rgba(0,0,0,0.10)',
                border: card.zIndex === 10
                  ? '1.5px solid rgba(255,255,255,0.92)'
                  : '1px solid rgba(255,255,255,0.75)',
                backgroundColor: '#ffffff',
              }}>
                <img
                  src={IMAGES[card.imgIndex]}
                  alt=""
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                  draggable={false}
                />
              </div>
            </div>
          );
        })}

        {/* Brand + animated progress bar */}
        <div style={{ position: 'relative', zIndex: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <span style={{
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.12em',
            color: '#1e293b',
            fontFamily: 'system-ui, sans-serif',
            textTransform: 'uppercase',
          }}>
            Scubamail
          </span>

          {/* Progress bar — CSS animation fills 0→100% over 3s */}
          <div style={{
            width: 140,
            height: 3,
            borderRadius: 999,
            background: 'rgba(0,0,0,0.09)',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              borderRadius: 999,
              background: 'linear-gradient(90deg, #6366f1 0%, #a78bfa 50%, #818cf8 100%)',
              backgroundSize: '200% 100%',
              animation: 'bl-progress 3s cubic-bezier(0.25, 0.1, 0.25, 1) forwards',
            }} />
          </div>
        </div>
      </div>
    </>
  );
};
